import api from '../../../shared/services/api';
import { API_ENDPOINTS } from '../../../shared/constants/apiEndpoints';

// Global cache for cluster data - persists across component unmounts
const globalClusterCache = {
  clusters: [],
  clustersWithTargets: [],
  lastFetchTime: null,
  isValid: false
};

// Cache validity duration (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Service for managing FMO cluster data
 */
export const fmoClusterService = {
  /**
   * Check if cache is valid
   * @returns {boolean} Whether the cache is valid
   */
  isCacheValid() {
    if (!globalClusterCache.isValid || !globalClusterCache.lastFetchTime) {
      return false;
    }
    
    const now = Date.now();
    const cacheAge = now - globalClusterCache.lastFetchTime;
    return cacheAge < CACHE_DURATION;
  },

  /**
   * Get cached data if valid
   * @returns {Object|null} Cached data or null if invalid
   */
  getCachedData() {
    if (this.isCacheValid()) {
      return {
        clusters: globalClusterCache.clusters,
        clustersWithTargets: globalClusterCache.clustersWithTargets
      };
    }
    return null;
  },

  /**
   * Invalidate the cache
   */
  invalidateCache() {
    globalClusterCache.isValid = false;
  },

  /**
   * Fetch all clusters with pagination
   * @returns {Promise<Array>} Array of cluster objects
   */
  async fetchAllClusters() {
    let allClusters = [];
    let offset = 0;
    const limit = 1000;
    
    do {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      
      const clustersRes = await api.get(`${API_ENDPOINTS.CLUSTERS.BASE}?${params.toString()}`);
      const data = clustersRes.data;
      
      if (data.items && Array.isArray(data.items)) {
        allClusters = [...allClusters, ...data.items];
        
        // Check if we got all items
        if (data.items.length < limit) {
          break;
        }
        
        offset += limit;
      } else {
        break;
      }
    } while (true);
    
    console.log(`Fetched ${allClusters.length} clusters`);
    return allClusters;
  },

  /**
   * Map clusters to their target information
   * @param {Array} clusters - Array of cluster objects
   * @param {Array} clusterspecs - Array of clusterspec objects for mapping
   * @returns {Promise<Array>} Array of clusters with target information
   */
  async mapClustersToTargets(clusters, clusterspecs = []) {
    const clustersWithTargetInfo = await Promise.all(
      clusters.map(async (cluster) => {
        try {
          // First, get the full cluster details which includes the group
          const clusterDetailRes = await api.get(API_ENDPOINTS.CLUSTERS.BY_ID(cluster.id_));
          const clusterDetail = clusterDetailRes.data;
          
          // Check if cluster has group with prep_ids
          if (clusterDetail.group && clusterDetail.group.prep_ids && clusterDetail.group.prep_ids.length > 0) {
            // Get target from first prep
            const prepId = clusterDetail.group.prep_ids[0];
            const prepRes = await api.get(API_ENDPOINTS.PREPS.BY_ID(prepId));
            const prep = prepRes.data;
            
            // Get clusterspec name
            const clusterspec = clusterspecs.find(spec => spec.id_ === clusterDetail.clusterspec_id);
            const clusterspecName = clusterspec ? clusterspec.name : `Spec ${clusterDetail.clusterspec_id}`;
            
            return {
              ...cluster,
              target: prep.target || '<none>',
              groupName: cluster.name || `Cluster ${cluster.id_}`,
              memberCount: clusterDetail.group.prep_ids.length,
              residueCount: clusterDetail.residues ? clusterDetail.residues.length : 0,
              clusterspecName: clusterspecName
            };
          }
          
          return {
            ...cluster,
            target: '<none>',
            groupName: cluster.name || `Cluster ${cluster.id_}`,
            memberCount: 0,
            residueCount: 0,
            clusterspecName: 'Unknown'
          };
        } catch (error) {
          console.error(`Failed to get target for cluster ${cluster.id_}:`, error);
          return {
            ...cluster,
            target: '<unknown>',
            groupName: cluster.name || `Cluster ${cluster.id_}`,
            memberCount: 0,
            residueCount: 0,
            clusterspecName: 'Unknown'
          };
        }
      })
    );
    
    return clustersWithTargetInfo;
  },

  /**
   * Fetch clusters and map to targets with caching
   * @param {Array} clusterspecs - Array of clusterspec objects for mapping
   * @param {boolean} forceRefresh - Force refresh ignoring cache
   * @returns {Promise<Object>} Object containing clusters and clustersWithTargets
   */
  async fetchClustersWithTargets(clusterspecs = [], forceRefresh = false) {
    // Check cache first unless forced refresh
    if (!forceRefresh) {
      const cached = this.getCachedData();
      if (cached) {
        return cached;
      }
    } else {
      this.invalidateCache();
    }
    
    try {
      // Fetch all clusters
      const clusters = await this.fetchAllClusters();
      
      // Map clusters to targets
      const clustersWithTargets = await this.mapClustersToTargets(clusters, clusterspecs);
      
      // Update cache
      globalClusterCache.clusters = clusters;
      globalClusterCache.clustersWithTargets = clustersWithTargets;
      globalClusterCache.lastFetchTime = Date.now();
      globalClusterCache.isValid = true;
      
      return {
        clusters,
        clustersWithTargets
      };
    } catch (error) {
      console.error('Failed to fetch cluster data:', error);
      throw error;
    }
  },

  /**
   * Get cluster details by ID
   * @param {string} clusterId - The cluster ID
   * @returns {Promise<Object>} Cluster details object
   */
  async getClusterDetails(clusterId) {
    try {
      const response = await api.get(API_ENDPOINTS.CLUSTERS.BY_ID(clusterId));
      return response.data;
    } catch (error) {
      console.error('Failed to fetch cluster details:', error);
      throw error;
    }
  },

  /**
   * Update cluster information
   * @param {string} clusterId - The cluster ID
   * @param {string} name - New cluster name
   * @param {string} description - New cluster description
   * @returns {Promise<void>}
   */
  async updateCluster(clusterId, name, description) {
    try {
      // Build update request - API expects an array
      const updateRequest = [{
        id_: clusterId,
        name: name?.trim() || null,
        description: description?.trim() || null
      }];
      
      await api.put(API_ENDPOINTS.CLUSTERS.BASE, updateRequest);
      
      // Update cache if valid
      if (globalClusterCache.isValid) {
        globalClusterCache.clusters = globalClusterCache.clusters.map(c =>
          c.id_ === clusterId
            ? { ...c, name: name?.trim(), description: description?.trim() }
            : c
        );
        
        globalClusterCache.clustersWithTargets = globalClusterCache.clustersWithTargets.map(c =>
          c.id_ === clusterId
            ? { ...c, name: name?.trim(), groupName: name?.trim(), description: description?.trim() }
            : c
        );
        
      }
    } catch (error) {
      console.error('Failed to update cluster:', error);
      throw error;
    }
  },

  /**
   * Filter clusters by target
   * @param {Array} clusters - Array of clusters with target info
   * @param {string} target - Target to filter by
   * @returns {Array} Filtered clusters
   */
  filterClustersByTarget(clusters, target) {
    if (!target) return clusters;
    return clusters.filter(cluster => cluster.target === target);
  },

  /**
   * Create a new empty cluster (residue set)
   * @param {Object} params - Creation parameters
   * @param {string} params.name - Cluster name
   * @param {string} params.description - Cluster description
   * @param {string} params.target - Target name
   * @param {number} params.groupId - Group ID
   * @returns {Promise<number>} Created cluster ID
   */
  async createEmptyCluster({ name, description, target, groupId }) {
    try {
      const clusterData = [{
        name: name,
        description: description || '',
        mode: 'manual',
        tags: [`target:${target}`, 'manual:true', 'empty:true'],
        group_id: groupId
      }];
      
      let response = await api.post(API_ENDPOINTS.CLUSTERS.BASE, clusterData);
      
      // Poll if 202 Accepted
      let pollCount = 0;
      const maxPolls = 60;
      const pollInterval = 1000;
      
      while (response.status === 202 && pollCount < maxPolls) {
        pollCount++;
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        response = await api.post(API_ENDPOINTS.CLUSTERS.BASE, clusterData);
      }
      
      if (response.status === 202) {
        throw new Error('Cluster creation timeout');
      }
      
      // Invalidate cache after creation
      this.invalidateCache();
      
      // Extract cluster ID from response
      if (response.data.workflow?.result?.items?.[0]) {
        return response.data.workflow.result.items[0].id_;
      } else if (Array.isArray(response.data)) {
        return response.data[0].id_;
      } else {
        return response.data.id_;
      }
    } catch (error) {
      console.error('Failed to create empty cluster:', error);
      throw error;
    }
  },

  /**
   * Create a manual cluster from existing cluster with selected residues
   * @param {Object} params - Creation parameters
   * @param {string} params.name - Cluster name
   * @param {string} params.description - Cluster description
   * @param {string} params.target - Target name
   * @param {number} params.groupId - Group ID
   * @param {number} params.sourceClusterId - Source cluster to copy from
   * @param {Array<number>} params.residues - Selected residue indices
   * @returns {Promise<number>} Created cluster ID
   */
  async createManualCluster({ name, description, target, groupId, sourceClusterId, residues }) {
    try {
      const clusterData = [{
        name: name,
        description: description || '',
        mode: 'manual',
        group_id: groupId,
        clusterspec_id: null,  // Manual clusters don't have a clusterspec
        residues: residues,     // Array of residue strings in format "chain:resName:resNum:insertionCode"
        tags: [`target:${target}`, 'manual:true', `source:${sourceClusterId}`]
      }];
      
      let response = await api.post(API_ENDPOINTS.CLUSTERS.BASE, clusterData);
      
      // Poll if 202 Accepted
      let pollCount = 0;
      const maxPolls = 60;
      const pollInterval = 1000;
      
      while (response.status === 202 && pollCount < maxPolls) {
        pollCount++;
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        response = await api.post(API_ENDPOINTS.CLUSTERS.BASE, clusterData);
      }
      
      if (response.status === 202) {
        throw new Error('Cluster creation timeout');
      }
      
      // Invalidate cache after creation
      this.invalidateCache();
      
      // Extract cluster ID from response
      if (response.data.workflow?.result?.items?.[0]) {
        return response.data.workflow.result.items[0].id_;
      } else if (Array.isArray(response.data)) {
        return response.data[0].id_;
      } else {
        return response.data.id_;
      }
    } catch (error) {
      console.error('Failed to create manual cluster:', error);
      throw error;
    }
  }
};